import { db } from './firebase';
import { deleteStripeCustomerId } from './firestore';
import { collection, addDoc, onSnapshot, doc, setDoc, serverTimestamp, getFirestore } from 'firebase/firestore';

// Active IDs for creating NEW subscriptions
export const PRICE_IDS = {
    supporter: 'price_1SenFSRGMToBbrNoBP37LlSt', // $5/month
    super_fan: 'price_1SenG6RGMToBbrNo3JaHh5TH'  // $10/month
};

// All valid IDs for checking status (Legacy + Active)
// We will populate these once identified
export const VALID_PRICE_IDS = {
    supporter: [PRICE_IDS.supporter, 'price_1Scx2fIKvzGMBcwXhs5bguPs'], // Guessing 2f is Supporter?
    super_fan: [PRICE_IDS.super_fan, 'price_1Scx5NIKvzGMBcwXLzmGfeDY']  // Guessing 5N is Super Fan?
};

export const checkSubscriptionTier = (priceId) => {
    if (VALID_PRICE_IDS.super_fan.includes(priceId)) return 'super_fan';
    if (VALID_PRICE_IDS.supporter.includes(priceId)) return 'supporter';
    return null;
};

export const GIFT_PRICE_IDS = {
    supporter: 'price_1SenGnRGMToBbrNofc5I7mhs', // $5 (One-time)
    super_fan: 'price_1SenHPRGMToBbrNoPjqc7IhL'  // $10 (One-time)
};

/**
 * Creates a ONE-TIME checkout session for a Gift
 */
export const createGiftCheckoutSession = async (uid, tier = 'super_fan', recipientId = null, recipientName = null) => {
    console.log("Starting createGiftCheckoutSession...", { uid, tier, recipientId });
    const firestore = getFirestore();
    const collectionRef = collection(firestore, 'customers', uid, 'checkout_sessions');

    const priceId = tier === 'supporter' ? GIFT_PRICE_IDS.supporter : GIFT_PRICE_IDS.super_fan;
    console.log("Using Price ID:", priceId);

    const successUrl = recipientId
        ? `${window.location.origin}/gift-success?session_id={CHECKOUT_SESSION_ID}&recipient_id=${recipientId}`
        : `${window.location.origin}/gift-success?session_id={CHECKOUT_SESSION_ID}`;

    try {
        console.log("Adding checkout session doc...");
        const docRef = await addDoc(collectionRef, {
            mode: 'payment', // One-time payment
            // price: priceId, // REPLACED with line_items for dynamic description
            line_items: [
                {
                    price: priceId, // Using the explicit Price ID provided by config
                    quantity: 1
                }
            ],
            success_url: successUrl, // Use the variable we created earlier
            cancel_url: `${window.location.origin}/support`,
            metadata: {
                isGift: 'true',
                giftTier: tier,
                recipientId: recipientId || '',
                recipientName: recipientName || ''
            },
            payment_intent_data: {
                description: `Gift Subscription: ${tier === 'super_fan' ? 'Super Fan' : 'Supporter'} for ${recipientName || 'Friend'}`
            }
        });
        console.log("Doc added with ID:", docRef.id);

        // Wait for the Extension to write the sessionId
        return new Promise((resolve, reject) => {
            console.log("Listening for updates on doc:", docRef.id);
            const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                console.log("Snapshot update:", data);

                if (!data) return;

                const { error, url } = data;
                if (error) {
                    unsubscribe();
                    console.error('Stripe Extension Error:', error);
                    reject(new Error(error.message));
                    return;
                }
                if (url) {
                    console.log("Redirect URL found:", url);
                    unsubscribe();
                    // Auto-redirect here or let caller do it? 
                    // The Promise resolves with URL.
                    // But in strict await, we might want to just redirect.
                    // The original code returned a Promise that resolves URL.
                    window.location.assign(url); // Force redirect
                    resolve(url);
                }
            });
        });

    } catch (error) {
        console.error('Error creating gift checkout session:', error);
        throw error;
    }
};

/**
 * Creates a Stripe Checkout Session via the Firebase Extension.
 * Writes to customers/{uid}/checkout_sessions
 */
export const createCheckoutSession = async (uid, priceId) => {
    if (!uid) throw new Error("User ID required");

    // The extension listens to the 'customers' collection by default
    const sessionsRef = collection(db, 'customers', uid, 'checkout_sessions');

    try {
        // Create a new checkout session doc
        const docRef = await addDoc(sessionsRef, {
            price: priceId,
            success_url: window.location.origin + '/profile',
            cancel_url: window.location.origin + '/subscription',
            mode: 'subscription', // or 'payment' for one-time
            // Metadata can be useful but optional
            metadata: {
                firebase_uid: uid
            },
            subscription_data: {
                description: priceId === PRICE_IDS.super_fan ? 'Spark Inspo: Super Fan Tier (Monthly)' : 'Spark Inspo: Supporter Tier (Monthly)',
                metadata: {
                    firebase_uid: uid,
                    tier: priceId === PRICE_IDS.super_fan ? 'super_fan' : 'supporter'
                }
            }
        });

        console.log("Checkout session doc created:", docRef.id);

        // Listen for the 'url' or 'error' field
        return new Promise((resolve, reject) => {
            const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                console.log("Stripe Session Snapshot:", JSON.stringify(data, null, 2)); // DEBUG LOG: Expanded
                if (!data) return; // Wait for data

                if (data.error) {
                    unsubscribe();
                    console.error("Stripe Extension Error:", data.error);

                    // Handle "No such customer" (Test Mode vs Live Mode mismatch)
                    if (data.error.message && data.error.message.includes("No such customer")) {
                        console.log("Detected invalid Stripe Customer ID. Resetting and retrying...");

                        // Delete the invalid ID
                        deleteStripeCustomerId(uid).then(() => {
                            console.log("Stripe ID reset. Retrying checkout automatically...");
                            // Recursive retry!
                            resolve(createCheckoutSession(uid, priceId));
                        });
                        return;
                    }

                    reject(new Error(data.error.message));
                }

                if (data.url) {
                    console.log("Stripe Redirect URL found:", data.url); // DEBUG LOG
                    unsubscribe();
                    resolve(data.url);
                }
            });
        });
    } catch (error) {
        console.error("Error creating checkout session:", error);
        throw error;
    }
};

/**
 * Creates a Portal Link for managing subscriptions.
 * Writes to customers/{uid}/portal_links
 */
export const createPortalLink = async (uid) => {
    if (!uid) throw new Error("User ID required");

    const linksRef = collection(db, 'customers', uid, 'portal_links');

    try {
        const docRef = await addDoc(linksRef, {
            return_url: window.location.origin + '/profile'
        });

        return new Promise((resolve, reject) => {
            const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                if (!data) return;

                if (data.error) {
                    unsubscribe();
                    reject(new Error(data.error.message));
                }

                if (data.url) {
                    unsubscribe();
                    resolve(data.url);
                }
            });
        });
    } catch (error) {
        console.error("Error creating portal link:", error);
        throw error;
    }
};

/**
 * Manually creates a Stripe Customer record if it doesn't exist.
 * Useful for syncing existing users to the Extension.
 */
export const syncStripeCustomer = async (uid, email, name) => {
    if (!uid) throw new Error("User ID required");

    const customerRef = doc(db, 'customers', uid);

    // Check if already exists to avoid overwriting valuable data
    // tailored to the extension's behavior (it listens for writes)
    try {
        await setDoc(customerRef, {
            email: email,
            name: name,
            created_at: serverTimestamp() // Force a write event
        }, { merge: true });
        console.log("Stripe Customer Sync triggered for:", uid);
        return true;
    } catch (error) {
        console.error("Error syncing Stripe Customer:", error);
        throw error;
    }
};
